"use client";

/**
 * Animated illustrations of BB's tools for the event screens (Gordon
 * 2026-11-03: short films about every part of the platform, for music
 * makers). Drawn in CSS/SVG, sized in container units (cqw) so they scale
 * with the 16:9 stage, black & white like /launch: the artwork is the only
 * colour (Gordon 2026-11-03). Real covers and people
 * are passed in; the only text is numbers.
 */

import Image from "next/image";
import { cn } from "@/lib/utils";
import type { FilmAudioTrack } from "./film-art";
import { RealDecks, RealLoudness, RealTrack, ScrollWave, pickPair, pickTrack } from "./real-audio";

const FG = "#f5f1ea";

export type MockId =
  | "wave" | "price" | "donut" | "decks" | "pads" | "timeline" | "layers" | "meter"
  | "staff" | "film" | "chat" | "doc" | "ticket" | "flow" | "faces" | "screens" | "keys" | "rec" | "qr"
  | "photos" | "calendar" | "ledger";

export interface MockProps {
  covers: string[];
  people: string[];
  /** Ready-formatted strings a mock shows (prices, chat lines …). */
  lines?: string[];
  /** Real BB tracks, measured (film-art.ts) — every audio graphic is drawn from these. */
  audio?: FilmAudioTrack[];
  /** Picks which real track a beat shows. */
  seed?: number;
}

export const MOCK_CSS = `
@keyframes sm-bar { 0%,100% { transform: scaleY(.25) } 50% { transform: scaleY(1) } }
@keyframes sm-spin { to { transform: rotate(360deg) } }
@keyframes sm-slide { 0%,100% { left: 18% } 50% { left: 74% } }
@keyframes sm-pad { 0%,100% { opacity: .18 } 8% { opacity: 1 } 30% { opacity: .35 } }
@keyframes sm-head { from { left: 0% } to { left: 100% } }
@keyframes sm-in { from { opacity: 0; transform: translateY(1.5cqw) } to { opacity: 1; transform: none } }
@keyframes sm-fan { from { transform: translate(0,0) rotate(0) } to { transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) } }
@keyframes sm-fill { from { transform: scaleY(0) } to { transform: scaleY(1) } }
@keyframes sm-dash { from { stroke-dashoffset: var(--len) } to { stroke-dashoffset: 0 } }
@keyframes sm-scan { 0% { top: 4% } 50% { top: 92% } 100% { top: 4% } }
@keyframes sm-key { 0%,100% { background: #fff } 10%,30% { background: #8c867d } }
@keyframes sm-pulse { 0%,100% { transform: scale(1); opacity: .9 } 50% { transform: scale(1.12); opacity: 1 } }
@keyframes sm-blink { 0%,100% { opacity: 1 } 50% { opacity: .25 } }
@keyframes sm-flash { 0% { opacity: 0 } 2% { opacity: .85 } 20%,100% { opacity: 0 } }
@keyframes sm-scroll { from { transform: translateX(0) } to { transform: translateX(var(--to)) } }
.sm-in { animation: sm-in .9s cubic-bezier(.22,.61,.36,1) both }
@media (prefers-reduced-motion: reduce) { [class*="sm-"] { animation-duration: .01s !important; animation-iteration-count: 1 !important } }
`;

const delay = (ms: number) => ({ animationDelay: `${ms}ms` });
const pick = <T,>(list: T[], i: number): T | undefined => (list.length ? list[i % list.length] : undefined);

function Cover({ src, className, rounded = true }: { src?: string; className?: string; rounded?: boolean }) {
  return (
    <div className={cn("relative overflow-hidden bg-white/10", rounded && "rounded-[0.6cqw]", className)}>
      {src && <Image src={src} alt="" fill sizes="25vw" className="object-cover" />}
    </div>
  );
}

export function Mock({ id, covers, people, lines = [], audio = [], seed = 0 }: MockProps & { id: MockId }) {
  const track = pickTrack(audio, seed);
  switch (id) {
    case "wave":
      return track ? <RealTrack track={track} /> : null;
    case "price":
      return (
        <div className="sm-in w-[40cqw] space-y-[2cqw]">
          <div className="flex items-end gap-[1.4cqw]">
            {covers.slice(0, 3).map((c, i) => <Cover key={i} src={c} className="h-[11cqw] w-[11cqw]" />)}
          </div>
          <div className="relative h-[1cqw] rounded-full bg-white/20">
            <span className="absolute top-1/2 h-[3cqw] w-[3cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-xl" style={{ animation: "sm-slide 5s ease-in-out infinite" }} />
          </div>
          <div className="flex justify-between text-[2.4cqw] font-bold tabular-nums">
            {lines.slice(0, 3).map((l, i) => <span key={i} className="sm-in" style={delay(300 + i * 250)}>{l}</span>)}
          </div>
        </div>
      );
    case "donut": {
      const parts = [50, 30, 20];
      const r = 15.915;
      let acc = 0;
      return (
        <div className="sm-in flex items-center gap-[3cqw]">
          <svg viewBox="0 0 42 42" className="h-[26cqw] w-[26cqw] -rotate-90">
            <circle cx="21" cy="21" r={r} fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="6" />
            {parts.map((p, i) => {
              const el = (
                <circle key={i} cx="21" cy="21" r={r} fill="none" stroke={FG} strokeOpacity={1 - i * 0.3} strokeWidth="6"
                  strokeDasharray={`${p - 0.8} ${100 - p + 0.8}`} strokeDashoffset={-acc}
                  style={{ ["--len" as string]: `${p}`, animation: `sm-in .8s ease ${300 + i * 500}ms both` }} />
              );
              acc += p;
              return el;
            })}
          </svg>
          <ul className="space-y-[1.4cqw]">
            {parts.map((p, i) => (
              <li key={i} className="sm-in flex items-center gap-[1.2cqw]" style={delay(500 + i * 500)}>
                <Cover src={pick(people, i)} className="h-[6cqw] w-[6cqw] rounded-full" />
                <span className="text-[3.4cqw] font-bold tabular-nums">{p} %</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    case "decks": {
      const pair = pickPair(audio, seed);
      return pair ? <RealDecks a={pair[0]} b={pair[1]} /> : null;
    }
    case "pads":
      return (
        <div className="sm-in grid w-[34cqw] grid-cols-4 gap-[1cqw]">
          {Array.from({ length: 16 }, (_, i) => {
            const step = [0, 5, 10, 15, 2, 7, 8, 13, 4, 1, 14, 11, 6, 3, 12, 9][i];
            return <span key={i} className="block aspect-square rounded-[0.8cqw] bg-white" style={{ animation: `sm-pad 2s linear ${step * 125}ms infinite` }} />;
          })}
        </div>
      );
    case "timeline":
      return (
        <div className="sm-in w-[48cqw] space-y-[1cqw]">
          <Cover src={pick(covers, 5)} className="aspect-video w-full" />
          <div className="relative space-y-[0.6cqw] rounded-[0.6cqw] bg-white/5 p-[0.8cqw]">
            {[0, 1].map(row => (
              <div key={row} className="flex h-[3.4cqw] gap-[0.4cqw]">
                {[0, 1, 2, 3].map(c => (
                  <div key={c} className="sm-in relative overflow-hidden rounded-[0.4cqw]" style={{ flex: [3, 2, 4, 2][(c + row) % 4], ...delay(200 + (row * 4 + c) * 120) }}>
                    {row < 2 ? <Cover src={pick(covers, 6 + row * 4 + c)} className="absolute inset-0" rounded={false} /> : null}
                  </div>
                ))}
              </div>
            ))}
            {/* The audio track: a real BB recording */}
            {track && <ScrollWave track={track} seconds={10} head={0.4} mirror className="h-[3.4cqw] rounded-[0.4cqw] bg-white/10" />}
          </div>
        </div>
      );
    case "layers":
      return (
        <div className="relative h-[30cqw] w-[30cqw]">
          {[0, 1, 2].map(i => (
            <div key={i} className="absolute inset-0 overflow-hidden rounded-[0.8cqw] shadow-2xl ring-1 ring-white/20"
              style={{ ["--dx" as string]: `${(i - 1) * 7}cqw`, ["--dy" as string]: `${(1 - i) * 2}cqw`, ["--rot" as string]: `${(i - 1) * 7}deg`, animation: `sm-fan 1.4s cubic-bezier(.22,.61,.36,1) ${400 + i * 250}ms both`, zIndex: i }}>
              {i === 0 && <Cover src={pick(covers, 9)} className="absolute inset-0" rounded={false} />}
              {i === 1 && <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,.35),transparent_60%)]" />}
              {i === 2 && (
                <div className="absolute inset-0 flex flex-col justify-end p-[2cqw]">
                  <div className="mb-[0.8cqw] h-[2.4cqw] w-[70%] rounded bg-white" />
                  <div className="h-[1.4cqw] w-[45%] rounded bg-white/70" />
                </div>
              )}
            </div>
          ))}
        </div>
      );
    case "meter":
      return track ? <RealLoudness track={track} targetLabel={lines[1] || ""} /> : null;
    case "staff":
      return (
        <div className="sm-in w-[46cqw] rounded-[0.8cqw] bg-white p-[2cqw]">
          <div className="relative h-[12cqw]">
            {[0, 1, 2, 3, 4].map(l => <span key={l} className="absolute inset-x-0 h-[0.18cqw] bg-black" style={{ top: `${15 + l * 17}%` }} />)}
            {Array.from({ length: 10 }, (_, i) => (
              <span key={i} className="sm-in absolute h-[2.1cqw] w-[2.8cqw] -rotate-[20deg] rounded-[50%] bg-black" style={{ left: `${6 + i * 9}%`, top: `${8 + ((i * 3) % 7) * 10}%`, ...delay(300 + i * 280) }} />
            ))}
          </div>
          <div className="mt-[1.6cqw] flex h-[8cqw] gap-[0.2cqw]">
            {Array.from({ length: 14 }, (_, i) => (
              <span key={i} className="block flex-1 rounded-b-[0.3cqw] border border-black/40 bg-white" style={{ animation: `sm-key 2.8s linear ${((i * 5) % 14) * 200}ms infinite` }} />
            ))}
          </div>
        </div>
      );
    case "film":
      return (
        <div className="sm-in w-[48cqw] space-y-[1.2cqw]">
          <div className="relative aspect-[2.39/1] overflow-hidden rounded-[0.4cqw] bg-black">
            <Cover src={pick(covers, 12)} className="absolute inset-0 scale-110 grayscale" rounded={false} />
            <div className="absolute inset-x-0 top-0 h-[12%] bg-black" />
            <div className="absolute inset-x-0 bottom-0 h-[12%] bg-black" />
          </div>
          {track && <ScrollWave track={track} className="h-[8cqw]" />}
        </div>
      );
    case "chat":
      return (
        <div className="w-[44cqw] space-y-[1.4cqw]">
          {lines.map((l, i) => (
            <div key={i} className={cn("sm-in flex", i % 2 ? "justify-start" : "justify-end")} style={delay(300 + i * 1100)}>
              <span className={cn("max-w-[85%] rounded-[1.6cqw] px-[1.8cqw] py-[1cqw] text-[2.3cqw] font-semibold", i % 2 ? "bg-white/15 text-white" : "bg-white text-black")}>{l}</span>
            </div>
          ))}
        </div>
      );
    case "doc":
      return (
        <div className="sm-in relative w-[34cqw] rounded-[0.8cqw] bg-white p-[2.4cqw] shadow-2xl">
          {Array.from({ length: 11 }, (_, i) => (
            <div key={i} className={cn("mb-[1cqw] h-[0.9cqw] rounded-full", i === 4 || i === 8 ? "bg-black" : "bg-black/25")} style={{ width: `${55 + ((i * 29) % 45)}%`, ...(i === 4 || i === 8 ? { animation: "sm-blink 1.6s ease-in-out infinite" } : {}) }} />
          ))}
          <span className="absolute inset-x-0 h-[0.3cqw] bg-black/70 shadow-[0_0_1.5cqw_rgba(0,0,0,.5)]" style={{ animation: "sm-scan 4s ease-in-out infinite" }} />
        </div>
      );
    case "ticket":
      return (
        <div className="sm-in flex w-[46cqw] overflow-hidden rounded-[1cqw] bg-white text-black shadow-2xl">
          <Cover src={pick(covers, 14)} className="w-[44%] shrink-0" rounded={false} />
          <div className="flex flex-1 flex-col justify-between p-[2cqw]">
            <div className="space-y-[0.8cqw]">
              <div className="h-[1.6cqw] w-[80%] rounded bg-black" />
              <div className="h-[1cqw] w-[55%] rounded bg-black/40" />
              {lines[0] && <p className="pt-[0.6cqw] text-[2.2cqw] font-bold">{lines[0]}</p>}
            </div>
            <div className="grid h-[12cqw] w-[12cqw] grid-cols-7 gap-[0.2cqw] self-end" style={{ animation: "sm-pulse 2.4s ease-in-out infinite" }}>
              {Array.from({ length: 49 }, (_, i) => <span key={i} className={((i * 7 + (i >> 2)) % 3) ? "bg-black" : "bg-transparent"} />)}
            </div>
          </div>
        </div>
      );
    case "flow":
      return (
        <svg viewBox="0 0 100 60" className="sm-in h-[30cqw] w-[50cqw]">
          {[12, 30, 48].map((y, i) => (
            <path key={y} d={`M18 30 C 45 30, 45 ${y}, 72 ${y}`} fill="none" stroke={FG} strokeWidth=".7" strokeDasharray="80" style={{ ["--len" as string]: "80", animation: `sm-dash 1.2s ease ${500 + i * 400}ms both` }} />
          ))}
          <circle cx="12" cy="30" r="7" fill={FG} />
          <path d="M12 26 v8 M8.5 29.5 L12 26 L15.5 29.5" stroke="#171512" strokeWidth="1.4" fill="none" strokeLinecap="round" />
          {[12, 30, 48].map((y, i) => <circle key={y} cx="78" cy={y} r="5.5" fill="none" stroke={FG} strokeWidth="1" style={{ animation: `sm-in .6s ease ${1200 + i * 400}ms both` }} />)}
        </svg>
      );
    case "faces":
      return (
        <div className="grid w-[46cqw] grid-cols-5 gap-[0.8cqw]">
          {people.slice(0, 15).map((p, i) => (
            <div key={p} className="sm-in" style={delay(i * 110)}>
              <Cover src={p} className="aspect-square rounded-full grayscale" />
            </div>
          ))}
        </div>
      );
    case "screens":
      return (
        <div className="grid w-[50cqw] grid-cols-2 gap-[1.2cqw]">
          {lines.slice(0, 4).map((src, i) => (
            <div key={src} className="sm-in overflow-hidden rounded-[0.6cqw] border border-white/25 bg-black shadow-2xl" style={delay(300 + i * 400)}>
              <div className="flex h-[1.2cqw] items-center gap-[0.3cqw] bg-white/10 px-[0.6cqw]">{[0, 1, 2].map(d => <span key={d} className="h-[0.45cqw] w-[0.45cqw] rounded-full bg-white/40" />)}</div>
              <div className="relative aspect-[16/10]"><Image src={src} alt="" fill sizes="25vw" className="object-cover object-top" /></div>
            </div>
          ))}
        </div>
      );
    case "keys":
      return (
        <div className="sm-in relative flex h-[16cqw] w-[46cqw] gap-[0.25cqw]">
          {Array.from({ length: 15 }, (_, i) => (
            <span key={i} className="block flex-1 rounded-b-[0.4cqw] bg-white" style={{ animation: `sm-key 3.2s linear ${[0, 4, 7, 2, 9, 11, 5][i % 7] * 230}ms infinite` }} />
          ))}
          {[0, 1, 3, 4, 5, 7, 8, 10, 11, 12].map(i => (
            <span key={`b${i}`} className="absolute top-0 h-[60%] w-[3.4%] rounded-b-[0.3cqw] bg-black" style={{ left: `${(i + 1) * (100 / 15) - 1.7}%` }} />
          ))}
        </div>
      );
    case "qr": {
      // lines[0] = the address. ecc=H: the BB mark in the middle still scans.
      const url = lines[0] || "https://www.breakinbread.com";
      return (
        <div className="sm-in relative rounded-[1cqw] bg-white p-[1.6cqw] shadow-2xl">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=0&ecc=H&data=${encodeURIComponent(url)}`} alt="" className="block h-[26cqw] w-[26cqw]" />
          <span className="absolute left-1/2 top-1/2 flex h-[6cqw] w-[6cqw] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[0.8cqw] bg-white">
            <span className="block h-[4.6cqw] w-[4.6cqw] bg-black" style={{ WebkitMaskImage: "url(/brand/bb-logo.svg)", maskImage: "url(/brand/bb-logo.svg)", WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat", WebkitMaskPosition: "center", maskPosition: "center" }} />
          </span>
        </div>
      );
    }
    case "photos":
      // A contact sheet: the viewfinder lands on one frame, the flash goes off.
      return (
        <div className="relative w-[44cqw]">
          <div className="grid grid-cols-4 gap-[0.6cqw]">
            {Array.from({ length: 12 }, (_, i) => (
              <div key={i} className="sm-in" style={delay(i * 110)}>
                <Cover src={pick(people.length ? people : covers, i)} className="aspect-square" />
              </div>
            ))}
          </div>
          <span className="absolute left-[25.3%] top-[33.9%] h-[32.1%] w-[24%] rounded-[0.5cqw] ring-[0.35cqw] ring-white" style={{ animation: "sm-pulse 2.4s ease-in-out infinite" }} />
          <span className="pointer-events-none absolute inset-0 rounded-[0.6cqw] bg-white opacity-0" style={{ animation: "sm-flash 3s ease-out 1.4s infinite" }} />
        </div>
      );
    case "calendar": {
      // A month of nights; booked ones fill with the artists' covers.
      const booked = [3, 9, 12, 17, 20, 26];
      return (
        <div className="sm-in w-[42cqw] rounded-[0.8cqw] bg-white/5 p-[1.4cqw] ring-1 ring-white/15">
          <div className="grid grid-cols-7 gap-[0.6cqw]">
            {Array.from({ length: 28 }, (_, i) => {
              const b = booked.indexOf(i);
              return (
                <div key={i} className="relative aspect-square overflow-hidden rounded-[0.4cqw] bg-white/10">
                  {b >= 0 && (
                    <div className="sm-in absolute inset-0" style={delay(500 + b * 450)}>
                      <Cover src={pick(covers, b + 2)} className="absolute inset-0" rounded={false} />
                    </div>
                  )}
                  <span className="absolute left-[10%] top-[4%] text-[1.3cqw] font-bold tabular-nums [text-shadow:0_0_0.6cqw_rgba(0,0,0,.9)]">{i + 1}</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    case "ledger":
      // Receipts in, sales in: the books keep themselves.
      return (
        <div className="w-[42cqw] space-y-[1cqw]">
          {lines.slice(0, 4).map((l, i) => (
            <div key={i} className="sm-in flex items-center gap-[1.4cqw] rounded-[0.6cqw] bg-white/10 px-[1.4cqw] py-[1cqw]" style={delay(300 + i * 500)}>
              <span className="flex h-[4.4cqw] w-[3.4cqw] shrink-0 flex-col justify-center gap-[0.35cqw] rounded-[0.3cqw] bg-white px-[0.5cqw]">
                {[80, 60, 70].map((w, k) => <span key={k} className="block h-[0.35cqw] rounded-full bg-black/40" style={{ width: `${w}%` }} />)}
              </span>
              <span className="flex-1 space-y-[0.5cqw]">
                <span className="block h-[0.9cqw] w-[70%] rounded-full bg-white/70" />
                <span className="block h-[0.7cqw] w-[40%] rounded-full bg-white/35" />
              </span>
              <span className="text-[2.4cqw] font-bold tabular-nums">{l}</span>
            </div>
          ))}
        </div>
      );
    case "rec":
      return (
        <div className="sm-in w-[44cqw] space-y-[1.4cqw]">
          {[0, 1, 2].map(t => (
            <div key={t} className="flex items-center gap-[1.2cqw]">
              <Cover src={pick(people, t)} className="h-[6cqw] w-[6cqw] shrink-0 rounded-full grayscale" />
              {pickTrack(audio, seed + t) ? <ScrollWave track={pickTrack(audio, seed + t)!} seconds={7} head={0.85} className="h-[6cqw] flex-1" /> : <div className="h-[6cqw] flex-1" />}
              <span className="h-[1.6cqw] w-[1.6cqw] shrink-0 rounded-full bg-[#f5f1ea]" style={{ animation: "sm-blink 1.2s ease-in-out infinite" }} />
            </div>
          ))}
        </div>
      );
  }
}
